/**
 * "Agent inputs": for every field the agent takes, where its value comes from —
 *
 * - **Dataset**: a column, so it changes per item;
 * - **Fixed**: one value for the whole experiment (`const:` in the mapping);
 * - **Credential**: a saved secret, decrypted only when the agent is called
 *   (`credential_refs`; the experiment never holds the value).
 *
 * Rows are grouped by where the connector puts each field in its request
 * (`InputField.target`), so the form mirrors the request as sent. Nothing is
 * pre-filled or guessed, and nothing is required: an unmapped field is not
 * sent, and the platform judges its own request.
 * "JSON" edits the mapping as text; "Request" shows the first item's request.
 */

import { useState } from "react";
import { Field } from "@/components/ui/field";
import { JsonView } from "@/components/ui/json-view";
import { QueryState } from "@/components/ui/states";
import { Segmented } from "@/components/ui/tabs";
import type { AgentSpec, InputField } from "@/features/agents/types";
import type { RequestPreview } from "@/features/connectors/types";
import { useDatasetItems } from "@/features/datasets/hooks";
import type { DatasetItem } from "@/features/datasets/types";
import type { PathInfo } from "@/features/evaluators/types";
import { formatJson } from "@/lib/format";
import type { UseQueryResult } from "@tanstack/react-query";
import { InputRow, type Source } from "./input-row";
import { datasetFields, fixedSource, isFixed, parseMappingJson } from "./input-mapping";
import type { InputMapping } from "../types";

type View = "fields" | "json" | "request";

const VIEW_NOTE: Record<View, string> = {
  fields:
    "For each field the agent takes: read it from a dataset column, fix one value for the whole experiment, or use a saved credential.",
  json: "The same choices as JSON. A source is input.<column> or const:<value>; credentials are picked under Fields.",
  request:
    "What the agent is sent for the first dataset item. Secrets show as ••••; ids marked <set per call> are filled in when it runs.",
};

/** The agent's inputs, the dataset's fields (from its first page of items),
 *  that first item, and the mapping: the person's choices, nothing else. */
export function useInputMapping(
  spec: AgentSpec | undefined,
  datasetId: string | null,
  version: number | null,
  paths: PathInfo[],
  chosen: InputMapping | null,
) {
  const page = useDatasetItems(datasetId ?? "", datasetId ? version : null, 1).data;
  const sample = page?.items ?? [];
  const inputs = spec?.inputs ?? [];
  const keys = new Set(inputs.map((i) => i.key));
  const fields = datasetFields(sample, paths);
  // A column this dataset does not have (carried over from the agent's last
  // experiment on another dataset) reads as unmapped, not as a stale name.
  const known = (source: string) =>
    !page || !source.startsWith("input.") || fields.includes(source.slice("input.".length));
  return {
    inputs,
    fields,
    first: (sample[0] ?? null) as DatasetItem | null,
    // Only what is on screen: an entry for a field this version does not have
    // (a kept draft, another version) is never sent.
    mapping: Object.fromEntries(
      Object.entries(chosen ?? {}).filter(([k, source]) => (!spec || keys.has(k)) && known(source)),
    ),
  };
}

/** Inputs grouped by request target, in first-seen order. */
function byTarget(inputs: InputField[]): [string, InputField[]][] {
  const groups = new Map<string, InputField[]>();
  for (const input of inputs) groups.set(input.target, [...(groups.get(input.target) ?? []), input]);
  return [...groups];
}

/** Where an input's value comes from, as the form state says it. */
function sourceOf(input: InputField, mapping: InputMapping, refs: Record<string, string>): Source {
  if (input.key in refs) return "credential";
  return isFixed(mapping[input.key]) ? "fixed" : "dataset";
}

export interface CredentialRefs {
  refs: Record<string, string>;
  set: (key: string, id: string) => void;
  clear: (key: string) => void;
}

export function InputMappingStep({
  inputs,
  fields,
  mapping,
  onChange,
  credentials,
  preview,
}: {
  inputs: InputField[];
  fields: string[];
  mapping: InputMapping;
  onChange: (mapping: InputMapping) => void;
  credentials: CredentialRefs;
  /** The connector's request for the first item; absent when it cannot show one. */
  preview?: UseQueryResult<RequestPreview> | undefined;
}) {
  const [view, setView] = useState<View>("fields");
  const [json, setJson] = useState("");
  const [error, setError] = useState<string | null>(null);
  if (inputs.length === 0) return null;

  const editJson = (text: string) => {
    setJson(text);
    const parsed = parseMappingJson(text);
    if (typeof parsed === "string") setError(parsed);
    else (setError(null), onChange(parsed));
  };
  const pick = (next: View) => {
    setError(null);
    if (next === "json") setJson(formatJson(mapping));
    setView(next);
  };
  const without = (key: string) => Object.fromEntries(Object.entries(mapping).filter(([k]) => k !== key));
  const setSource = (input: InputField, source: Source) => {
    if (source === "credential") return (onChange(without(input.key)), credentials.set(input.key, ""));
    credentials.clear(input.key);
    onChange(
      source === "fixed"
        ? { ...mapping, [input.key]: fixedSource(input.options[0]?.value ?? "") }
        : without(input.key),
    );
  };

  return (
    <Field
      label="Agent inputs"
      aside={
        <Segmented
          label="Agent inputs view"
          value={view}
          onChange={pick}
          items={[
            { key: "fields", label: "Fields" },
            { key: "json", label: "JSON" },
            ...(preview ? [{ key: "request" as const, label: "Request" }] : []),
          ]}
        />
      }
      error={error}
    >
      <p className="-mt-0.5 text-xs leading-snug text-muted">{VIEW_NOTE[view]}</p>
      {view === "fields" && (
        <div className="overflow-hidden rounded-[9px] border border-line">
          {byTarget(inputs).map(([target, group]) => (
            <section
              key={target}
              aria-label={target || "request body"}
              className="border-line pb-1.5 [&+&]:border-t"
            >
              <h4 className="px-3 pt-2.5 pb-1 font-mono text-[11px] font-normal text-faint">
                {target || "request body"}
              </h4>
              {group.map((input) => (
                <InputRow
                  key={input.key}
                  input={input}
                  fields={fields}
                  source={sourceOf(input, mapping, credentials.refs)}
                  value={mapping[input.key] ?? ""}
                  credentialId={credentials.refs[input.key] ?? ""}
                  onSource={(s) => setSource(input, s)}
                  onChange={(v) => onChange({ ...mapping, [input.key]: v })}
                  onCredential={(id) => credentials.set(input.key, id)}
                />
              ))}
            </section>
          ))}
        </div>
      )}
      {view === "json" && (
        <textarea
          aria-label="Input mapping JSON"
          className="inp min-h-28 w-full font-mono text-xs"
          value={json}
          onChange={(e) => editJson(e.target.value)}
        />
      )}
      {view === "request" && preview && (
        <QueryState query={preview} what="request preview">
          <JsonView value={preview.data?.body} label="Request for the first item" openDepth={3} />
        </QueryState>
      )}
    </Field>
  );
}
